/**
 * Atlas PM · 项目星图 · 主容器（对齐高保真原型 `#starv`）
 *
 * 节点 = 任务；星座 = 任务形态聚类；星轨 = 任务依赖。
 *
 * ⭐ 原型（prototype/js/app.js `openStar`）：
 *     地图区/筛选/图例/排行/提示全部隐藏，`#starv` 全屏覆盖。
 *     星图页结构：全屏 canvas + 项目信息条 + 右侧「任务列表」
 *     + 左下「节点语义」图例 + 底部居中工具栏（5 按钮）+ 节点悬浮卡。
 *
 * ⭐ P5.4（用户要求优先于原型）：
 *     ① 去掉原型的 7 张全国 KPI 卡（`#kpibar`）——用户明确不要；
 *     ② 去掉信息条内独立面包屑，项目名改由顶栏 AppShell 面包屑拼接第三级
 *        （「全国态势 › 省 › 项目」）。
 *
 * 接入点：
 *   - useUiStore().currentProjectId 定位项目（从地图点进来）
 *   - 单击节点 / 任务列表行 → useUiStore().openTask(id) → 整页任务详情抽屉
 *     （P6.53：单击节点与点右侧任务卡行为完全一致，不再先弹小面板；
 *      双击不再单独处理——浏览器会先派发两次 click，结果同样是打开抽屉，幂等无副作用）
 *   - 顶部信息条「编辑项目」→ ProjectFormDialog(mode='edit')，按 project:update 权限显示/置灰
 *
 * ⚠️ Three.js 场景由 StarScene 管理，本组件只负责挂载 canvas、喂数据、传控件状态。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUiStore } from '../../stores/ui';
import { useAuthStore } from '../../stores/auth';
import { useFirstProject, useStarData } from './useStarData';
import { StarScene } from './scene';
import type { StarLayoutNode } from './layout';
import StarToolbar from './StarToolbar';
import StarHeader from './StarHeader';
import StarCategoryMetrics from './StarCategoryMetrics';
import StarTaskList from './StarTaskList';
import StarLegend, { starLegendBucketOf, type StarLegendKey } from './StarLegend';
import NodeInfoCard from './NodeInfoCard';
import ProjectFormDialog from '../project/ProjectFormDialog';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import './star.css';

interface HoverState {
  node: StarLayoutNode;
  x: number;
  y: number;
}

export default function StarView() {
  const navigate = useNavigate();
  const currentProjectId = useUiStore((s) => s.currentProjectId);
  const setCurrentProjectId = useUiStore((s) => s.setCurrentProjectId);
  const setCurrentProjectMeta = useUiStore((s) => s.setCurrentProjectMeta);
  const clearCurrentProjectMeta = useUiStore((s) => s.clearCurrentProjectMeta);
  const openTask = useUiStore((s) => s.openTask);
  const openTaskForm = useUiStore((s) => s.openTaskForm);
  const canUpdateProject = useAuthStore((s) => s.hasPermission('project:update'));

  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<StarScene | null>(null);

  const [autoRotate, setAutoRotate] = useState(true);
  const [showEdges, setShowEdges] = useState(true);
  const [showTrails, setShowTrails] = useState(true);
  const [hover, setHover] = useState<HoverState | null>(null);
  /* 项目编辑弹窗（P6.53：从星图信息条进入项目详情编辑） */
  const [projectEditorOpen, setProjectEditorOpen] = useState(false);
  /* ⭐ P6.53：单击星点 = 打开整页任务详情抽屉（与右侧任务卡一致）。
   *   highlightedTaskId 仍保留 —— 用于右侧任务列表行高亮（点节点 / 点任务卡都会设置）。
   *   P5.7 的任务速览小面板已随单击行为统一而移除。 */
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null);
  /* ⭐ P8.19：左下「节点语义」图例点击 → 仅筛选右侧任务列表（星图 3D 场景不受影响） */
  const [legendFilter, setLegendFilter] = useState<StarLegendKey | null>(null);

  /* 打开整页任务详情抽屉：高亮列表行 + 聚焦星点 */
  const openTaskFull = useCallback(
    (id: string) => {
      setHighlightedTaskId(id);
      sceneRef.current?.focus(id);
      openTask(id);
    },
    [openTask],
  );

  // 回调放进 ref，避免 scene 重建
  const handlers = useRef({
    openTask: openTaskFull,
    setHover,
  });
  handlers.current.openTask = openTaskFull;
  handlers.current.setHover = setHover;

  /* ── 未选项目时自动选第一个可见项目 ── */
  const firstProject = useFirstProject();
  useEffect(() => {
    if (currentProjectId) return;
    const first = firstProject.data?.items?.[0];
    if (first) setCurrentProjectId(first.id);
  }, [currentProjectId, firstProject.data, setCurrentProjectId]);

  const { project, tasks, edges, layout, deliverableReadyPct, isLoading, isError, error } =
    useStarData(currentProjectId);

  /* ── P5.4：把项目名 / 省份注册到 UI store，供顶栏面包屑拼接第三级 ── */
  useEffect(() => {
    if (!project) return;
    setCurrentProjectMeta({
      name: project.name,
      provinceCode: project.provinceCode,
      provinceName: project.provinceName,
    });
  }, [project, setCurrentProjectMeta]);

  /* 离开星图页时清掉，避免地图页面包屑残留项目名 */
  useEffect(() => () => clearCurrentProjectMeta(), [clearCurrentProjectMeta]);

  /* ── 创建 / 销毁场景（StrictMode 二次挂载也安全） ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = new StarScene(canvas, {
      onHover: (node, x, y) => {
        handlers.current.setHover(node ? { node, x, y } : null);
      },
      /* ⭐ P6.53：单击节点直接打开完整任务详情抽屉（与右侧任务卡一致）。
       * 不再注册 onDoubleClick —— 双击会自然触发两次 click，打开同一个抽屉，幂等。 */
      onClick: (node) => {
        if (node && !node.isCenter) handlers.current.openTask(node.id);
      },
    });
    sceneRef.current = ctx;
    return () => {
      ctx.dispose();
      sceneRef.current = null;
    };
  }, []);

  /* ── 数据变化 → 喂给场景 ── */
  useEffect(() => {
    sceneRef.current?.setData(layout, edges);
  }, [layout, edges]);

  useEffect(() => {
    sceneRef.current?.resetView();
    setHighlightedTaskId(null);
    setLegendFilter(null);
  }, [currentProjectId]);

  /* ── 工具栏状态 → 场景 ── */
  useEffect(() => {
    sceneRef.current?.setAutoRotate(autoRotate);
  }, [autoRotate]);
  useEffect(() => {
    sceneRef.current?.setShowEdges(showEdges);
  }, [showEdges]);
  useEffect(() => {
    sceneRef.current?.setShowTrails(showTrails);
  }, [showTrails]);

  /* ── 测试钩子：window.__STAR_OPEN_TASK__(id) 打开整页任务详情抽屉 ── */
  useEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    w.__STAR_OPEN_TASK__ = (id: string): void => {
      handlers.current.openTask(id);
    };
    return () => {
      delete w.__STAR_OPEN_TASK__;
    };
  }, []);

  /* ── 任务列表数据（去掉项目核心节点） ── */
  const taskNodes = useMemo(() => layout.nodes.filter((n) => !n.isCenter), [layout]);

  const visibleNodes = useMemo(
    () =>
      legendFilter == null
        ? taskNodes
        : taskNodes.filter(
            (n) =>
              starLegendBucketOf({ state: n.state, overdueDays: n.task?.overdueDays }) ===
              legendFilter,
          ),
    [taskNodes, legendFilter],
  );

  const counts = useMemo(() => {
    const buckets: Record<StarLegendKey, number> = {
      inProgress: 0,
      frozen: 0,
      approving: 0,
      rejectedOverdue: 0,
      draft: 0,
    };
    for (const t of tasks) {
      const key = starLegendBucketOf(t);
      if (key) buckets[key] += 1;
    }
    return {
      inProgress: buckets.inProgress,
      done: buckets.frozen,
      approving: buckets.approving,
      overdue: buckets.rejectedOverdue,
      notStarted: buckets.draft,
    };
  }, [tasks]);

  const onBack = useCallback(() => navigate('/map'), [navigate]);
  const onReset = useCallback(() => sceneRef.current?.resetView(), []);

  const noProject = !currentProjectId;
  /* ⭐ P8.3：区分「还在查项目」与「查完了但没有可见项目」。
   *   - 只有首次查询尚无结果（isPending 且未出错）才算 loading；
   *   - 已经有结果（哪怕 items 为空）就进入空状态，绝不无限转圈；
   *   - 故意不用 isFetching：后台刷新时不应把稳定的空状态翻回 spinner。 */
  const hasFirstProjectResult = firstProject.data !== undefined;
  const visibleProjectCount = firstProject.data?.items?.length ?? 0;
  const stillResolvingProject = noProject && !hasFirstProjectResult && !firstProject.isError;
  const noVisibleProjects = noProject && hasFirstProjectResult && visibleProjectCount === 0;
  const noTasks = !noProject && !isLoading && !isError && tasks.length === 0;

  return (
    <div className="starview" ref={containerRef}>
      <canvas ref={canvasRef} className="starview__canvas" />

      {!noProject && !isError && (
        <>
          <StarHeader
            project={project}
            taskTotal={tasks.length}
            taskFrozen={counts.done}
            taskOverdue={counts.overdue}
            deliverableReadyPct={deliverableReadyPct}
            onCreateTask={() => openTaskForm(currentProjectId ?? undefined)}
            canEditProject={canUpdateProject}
            onEditProject={() => setProjectEditorOpen(true)}
          />

          {/* ⭐ P8.10：信息条下方的空白带 —— 任务形态多选 + 所选形态完成度卡片。
              绝对定位、不占文档流，卡片区域为空时完全不渲染，星图布局不受影响。 */}
          <StarCategoryMetrics tasks={tasks} projectId={currentProjectId} />

          <StarTaskList
            nodes={visibleNodes}
            selectedId={highlightedTaskId}
            counts={counts}
            onSelect={openTaskFull}
            filterActive={legendFilter !== null}
            onClearFilter={() => setLegendFilter(null)}
          />

          <StarLegend
            active={legendFilter}
            onToggle={(key) => setLegendFilter((prev) => (prev === key ? null : key))}
          />

          <StarToolbar
            autoRotate={autoRotate}
            showEdges={showEdges}
            showTrails={showTrails}
            onToggleAutoRotate={setAutoRotate}
            onToggleEdges={setShowEdges}
            onToggleTrails={setShowTrails}
            onReset={onReset}
            onBack={onBack}
          />
        </>
      )}

      {hover && <NodeInfoCard node={hover.node} x={hover.x} y={hover.y} />}

      {/* 项目编辑弹窗（P6.53）：与地图页共用 ProjectFormDialog(mode=edit)，
          保存成功由 useUpdateProject 失效 ['projects'] 缓存，星图头部 / 地图标记 / 项目列表自动刷新 */}
      <ProjectFormDialog
        open={projectEditorOpen}
        mode="edit"
        projectId={currentProjectId ?? undefined}
        onClose={() => setProjectEditorOpen(false)}
        onSaved={() => setProjectEditorOpen(false)}
      />

      {noProject && (
        <div className="star-overlay">
          <div className="star-overlay__box">
            {stillResolvingProject ? (
              <Spinner label="正在选择项目…" />
            ) : noVisibleProjects ? (
              <>
                <div className="star-overlay__icon">✧</div>
                <div className="star-overlay__title">暂无可见项目</div>
                <div className="star-overlay__hint">
                  当前账号的数据范围内没有可访问的项目，因此暂时无法展示星图。如需查看项目，请联系管理员为你分配项目权限。
                </div>
                <Button variant="secondary" onClick={() => navigate('/map')}>
                  返回全国地图
                </Button>
              </>
            ) : (
              <>
                <div className="star-overlay__icon">✦</div>
                <div className="star-overlay__title">请先从全国地图选择项目</div>
                <div className="star-overlay__hint">
                  星图以项目为单位展示任务星座。前往「全国态势」点击项目光点，即可进入其星图。
                </div>
                <Button variant="primary" onClick={() => navigate('/map')}>
                  前往全国地图
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {!noProject && isLoading && (
        <div className="star-overlay">
          <Spinner label="正在加载任务星座…" />
        </div>
      )}

      {!noProject && isError && !isLoading && (
        <div className="star-overlay">
          <div className="star-overlay__box">
            <div className="star-overlay__icon">⚠</div>
            <div className="star-overlay__title">星图数据加载失败</div>
            <div className="star-overlay__err">{error?.message ?? '未知错误'}</div>
            <Button variant="secondary" onClick={() => navigate('/map')}>
              返回全国地图
            </Button>
          </div>
        </div>
      )}

      {noTasks && (
        <div className="star-overlay">
          <div className="star-overlay__box">
            <div className="star-overlay__icon">✧</div>
            <div className="star-overlay__title">该项目暂无任务</div>
            <div className="star-overlay__hint">创建任务后，任务将作为节点出现在星图中。</div>
            <Button variant="secondary" onClick={() => navigate('/map')}>
              返回全国地图
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
